/**
 * window.GTM — the dataLayer event helpers.
 *
 * 1:1 port of shopify-en-store-front app/lib/gtmEvent.ts, with the event names taken from
 * app/lib/const.ts. The names and payload keys are a contract with container GTM-PX8TJ3T9,
 * which already carries a Custom Event trigger per name and a Data Layer Variable per key
 * (verified against the container payload, 2026-09-02: view_album_detail_shop arrives with
 * locale / service_category / user_type / web_status / app_id / product_title). Renaming
 * anything here silently stops the Mixpanel tag firing, so the map below is the whole file.
 *
 * ONE KEY HAS BEEN RENAMED SINCE THAT VERIFICATION. The album identifier goes out as
 * `album_id` as of 2026-09-09; the payload above shows it under the old name `app_id`. Both
 * names carry the same value, the product's `custom.album_id` metafield. The send sites here
 * are sections/main-product-kit.liquid (view_album_detail_shop, click_add_to_cart,
 * click_buy_now) and assets/script-track-list.js (sample_play_shop). The container needs a
 * Data Layer Variable on `album_id` for those four events to keep reaching Mixpanel.
 *
 * The reference followed the same day, so no source sends `app_id` any more. Its nine send
 * sites — routes/($locale).products.$productHandle (view_album_detail_shop, sample_play_shop
 * x2, click_add_to_cart, click_buy_now), .../products.ooak.$productHandle (the first three)
 * and .../products.ooak.customize.$productHandle (click_buy_now) — all push `album_id` now,
 * which leaves the container's variable on the old key dead for every source.
 *
 * Five of the reference's 23 helpers are left out, all of them for things this theme cannot
 * observe. change_carousel_view and click_welcome_modal_cta belong to the Hydrogen welcome
 * modal, which has no equivalent here. complete_checkout is pushed from inside web pixel
 * 140214441's sandbox when checkout finishes, and no page in this theme can reach that
 * moment. view_shop_account and view_order_detail were wired to sections/main-account.liquid
 * and sections/main-order.liquid until they were removed on 2026-09-09, because no store this
 * theme serves ever renders those sections. store-us runs new customer accounts, so /account,
 * /account/login and /account/orders/<id> all 302 to order.kitbetter.com, whose hosted pages
 * carry neither theme JS nor the GTM container. store-jp and store-kr have customer accounts
 * switched off, so the same paths 302 to / instead. Both measured 2026-09-09; either way
 * templates/customers/* never render, which is the same wall complete_checkout hits. The only
 * moment this theme can observe there is the CLICK on the GNB profile menu's order-tracking
 * link ('gnb.liquid' / 'gnb-mobile.liquid'); if that ever becomes the definition it needs a
 * distinguishing key, because Hydrogen's is a page view.
 *
 * A helper for any of the five would only be something to call by mistake, so the remaining
 * 18 are exactly the events this theme can fire.
 *
 * Exposed as one global because assets/ files are classic scripts: a top-level `const` that
 * collides with another file takes this whole file down with it.
 */
(function () {
  const EVENT_NAMES = {
    clickShop: 'click_shop',
    clickShopBanner: 'click_shop_banner',
    clickBuyNow: 'click_buy_now',
    clickAddToCart: 'click_add_to_cart',
    clickSearch: 'click_search',
    clickBoardIcon: 'click_board_icon',
    clickPageNumber: 'click_page_number',
    samplePlayShop: 'sample_play_shop',
    viewAlbumDetailShop: 'view_album_detail_shop',
    viewGuestOrderPage: 'view_guest_order_page',
    viewGuestOrderDetail: 'view_guest_order_detail',
    viewCart: 'view_cart',
    viewCheckout: 'view_checkout',
    viewSearchResult: 'view_search_results_shop',
    searchProduct: 'search_product',
    sortCollection: 'sort_collection',
    sortSearchResult: 'sort_search_result',
    logout: 'sign_out',
  };

  // Reference: pushToDataLayer. The guard is the same one — a missing dataLayer means the
  // bootstrap snippet did not run, and dropping the event is better than throwing.
  const push = (event, data) => {
    if (!window.dataLayer) return;
    window.dataLayer.push(Object.assign({ event }, data || {}));
  };

  const GTM = { eventNames: EVENT_NAMES };

  Object.keys(EVENT_NAMES).forEach((helper) => {
    GTM[helper] = (data) => push(EVENT_NAMES[helper], data);
  });

  window.GTM = GTM;
})();
